import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Smartphone, 
  Laptop, 
  Tablet, 
  Plus, 
  RotateCcw, 
  Trash, 
  Lock, 
  MapPin, 
  Battery,
  Wifi,
  WifiOff,
  Shield,
  LogOut
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Device {
  id: string;
  name: string;
  os_type: string;
  os_version: string;
  status: string;
  last_seen: string;
  battery_level: number | null;
  battery_status: string | null;
  created_at: string;
}

interface DeviceAction {
  id: string;
  action_type: string;
  status: string;
  requested_at: string;
  completed_at: string | null;
  error_message: string | null;
}

const MyDevicesPage = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const debouncedFetchDevices = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchDevices();
      }, 500);
    };

    fetchDevices();
    
    // Set up real-time updates for device changes with proper filter
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      return supabase
        .channel('device-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'devices',
            filter: `portal_user_id=eq.${user.id}`
          },
          () => {
            debouncedFetchDevices();
          }
        )
        .subscribe();
    };

    const channelPromise = setupRealtimeSubscription();

    return () => {
      clearTimeout(timeoutId);
      channelPromise.then(channel => {
        if (channel) supabase.removeChannel(channel);
      });
    };
  }, []);

  const fetchDevices = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .eq('portal_user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (error: any) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceAction = async (deviceId: string, actionType: string) => {
    setActionLoading(deviceId);
    
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      // Insert device action record
      const { error } = await supabase
        .from('device_actions')
        .insert({
          device_id: deviceId,
          portal_user_id: user.user.id,
          action_type: actionType,
          status: 'pending'
        });

      if (error) throw error;

      // Call the device action edge function
      const { error: functionError } = await supabase.functions.invoke('portal-device-actions', {
        body: {
          device_id: deviceId,
          action_type: actionType
        }
      });

      if (functionError) throw functionError;

      toast.success(`${actionType} command sent to device`);
      fetchDevices(); // Refresh devices
    } catch (error: any) {
      console.error('Device action error:', error);
      toast.error(`Failed to ${actionType} device: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getDeviceIcon = (osType: string) => {
    switch (osType?.toLowerCase()) {
      case 'macos':
        return <Laptop className="h-6 w-6" />;
      case 'ios':
        return <Smartphone className="h-6 w-6" />;
      case 'ipados':
        return <Tablet className="h-6 w-6" />;
      default:
        return <Smartphone className="h-6 w-6" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      enrolled: { variant: 'default' as const, label: 'Enrolled' },
      pending_approval: { variant: 'secondary' as const, label: 'Pending Approval' },
      offline: { variant: 'destructive' as const, label: 'Offline' },
      active: { variant: 'default' as const, label: 'Active' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.offline;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/portal/login';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading your devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-2 sm:px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/lovable-uploads/5b1ac4fa-502a-48e9-b8b6-7c1fe37496e8.png" 
                alt="smplify Logo" 
                className="h-6 sm:h-8"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">My Devices</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Manage and monitor your enrolled devices
            </p>
          </div>
          <Link to="/portal/enroll" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Enroll Device</span>
              <span className="sm:hidden">Enroll</span>
            </Button>
          </Link>
        </div>

        {devices.length === 0 ? (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>No Devices Enrolled</CardTitle>
              <CardDescription>
                Get started by enrolling your first device
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Link to="/portal/enroll">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Enroll Your First Device
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {devices.map((device) => (
              <Card key={device.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getDeviceIcon(device.os_type)}
                      <div>
                        <CardTitle className="text-lg">{device.name}</CardTitle>
                        <CardDescription>
                          {device.os_type} {device.os_version}
                        </CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(device.status)}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Device Info */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Last Seen</span>
                      <div className="flex items-center">
                        {new Date(device.last_seen).getTime() > Date.now() - 300000 ? (
                          <Wifi className="h-3 w-3 text-green-500 mr-1" />
                        ) : (
                          <WifiOff className="h-3 w-3 text-gray-400 mr-1" />
                        )}
                        <span>{format(new Date(device.last_seen), 'MMM d, HH:mm')}</span>
                      </div>
                    </div>

                    {device.battery_level !== null && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Battery</span>
                        <div className="flex items-center">
                          <Battery className="h-3 w-3 mr-1" />
                          <span>{device.battery_level}%</span>
                          {device.battery_status && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({device.battery_status})
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {device.status === 'enrolled' && (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeviceAction(device.id, 'reboot')}
                        disabled={actionLoading === device.id}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Reboot
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeviceAction(device.id, 'lock')}
                        disabled={actionLoading === device.id}
                      >
                        <Lock className="h-3 w-3 mr-1" />
                        Lock
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeviceAction(device.id, 'locate')}
                        disabled={actionLoading === device.id}
                      >
                        <MapPin className="h-3 w-3 mr-1" />
                        Locate
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeviceAction(device.id, 'wipe')}
                        disabled={actionLoading === device.id}
                      >
                        <Trash className="h-3 w-3 mr-1" />
                        Wipe
                      </Button>
                    </div>
                  )}

                  {device.status === 'pending_approval' && (
                    <Alert>
                      <AlertDescription>
                        Device is pending admin approval before management actions are available.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDevicesPage;